import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type LexicalNode,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical';

import styles from './DiffNodes.module.css';

export type SerializedInsertNode = Spread<
  {
    type: 'insert';
  },
  SerializedTextNode
>;

export class InsertNode extends TextNode {
  static getType(): string {
    return 'insert';
  }

  static clone(node: InsertNode): InsertNode {
    return new InsertNode(node.__text, node.__key);
  }

  static importJSON(serializedNode: SerializedInsertNode): InsertNode {
    const node = $createInsertNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      ins: () => ({
        conversion: convertInsertElement,
        // Above default TextNode formatters (priority 0)
        priority: 4,
      }),
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('ins');
    element.className = styles.diffInsert;
    element.textContent = this.__text;
    return {element};
  }

  exportJSON(): SerializedInsertNode {
    return {
      ...super.exportJSON(),
      type: 'insert',
    };
  }

  createDOM(): HTMLElement {
    const element = document.createElement('ins');
    element.className = styles.diffInsert;
    element.textContent = this.__text;
    return element;
  }

  updateDOM(): boolean {
    return false;
  }
}

export function $createInsertNode(text: string): InsertNode {
  return $applyNodeReplacement(new InsertNode(text));
}

export function $isInsertNode(node: LexicalNode | null | undefined): node is InsertNode {
  return node instanceof InsertNode;
}

function convertInsertElement(domNode: HTMLElement): DOMConversionOutput {
  const textContent = domNode.textContent || '';
  if (textContent) {
    return {node: $createInsertNode(textContent)};
  }
  return {node: null};
}

export type SerializedDeleteNode = Spread<
  {
    type: 'delete';
  },
  SerializedTextNode
>;

export class DeleteNode extends TextNode {
  static getType(): string {
    return 'delete';
  }

  static clone(node: DeleteNode): DeleteNode {
    return new DeleteNode(node.__text, node.__key);
  }

  static importJSON(serializedNode: SerializedDeleteNode): DeleteNode {
    const node = $createDeleteNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      del: () => ({
        conversion: convertDeleteElement,
        priority: 4,
      }),
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('del');
    element.className = styles.diffDelete;
    element.textContent = this.__text;
    return {element};
  }

  exportJSON(): SerializedDeleteNode {
    return {
      ...super.exportJSON(),
      type: 'delete',
    };
  }

  createDOM(): HTMLElement {
    const element = document.createElement('del');
    element.className = styles.diffDelete;
    element.textContent = this.__text;
    return element;
  }

  updateDOM(): boolean {
    return false;
  }
}

export function $createDeleteNode(text: string): DeleteNode {
  return $applyNodeReplacement(new DeleteNode(text));
}

export function $isDeleteNode(node: LexicalNode | null | undefined): node is DeleteNode {
  return node instanceof DeleteNode;
}

function convertDeleteElement(domNode: HTMLElement): DOMConversionOutput {
  const textContent = domNode.textContent || '';
  if (textContent) {
    return {node: $createDeleteNode(textContent)};
  }
  return {node: null};
}
